import type { ReactNode } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { cn } from '@/lib/utils';

// Fractal noise as an inline SVG: the film grain of the Korza identity deck, no asset to load.
const GRAIN =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.55 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

const BLOBS = [
  {
    className: '-left-32 -top-40 h-[30rem] w-[30rem] bg-[#155DFC]',
    animate: { x: [0, 40, -20, 0], y: [0, 30, -10, 0] },
    duration: 18,
  },
  {
    className: '-right-24 top-1/2 h-[24rem] w-[24rem] bg-[#155DFC]',
    animate: { x: [0, -30, 20, 0], y: [0, -25, 15, 0] },
    duration: 22,
  },
];

/**
 * The dark stage the import happens on: #15191E fading into #0B348C, slow blue light and grain.
 * Blobs stand still with `prefers-reduced-motion`.
 */
export function ImportStage({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();

  return (
    <div
      data-slot="import-stage"
      className={cn(
        'relative isolate overflow-hidden rounded-2xl bg-[linear-gradient(155deg,#15191E_0%,#15191E_38%,#0B348C_130%)] text-white',
        className
      )}
    >
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        {BLOBS.map((blob) => (
          <motion.div
            key={blob.className}
            className={cn(
              'absolute rounded-full opacity-25 blur-3xl',
              blob.className
            )}
            animate={reduceMotion ? undefined : blob.animate}
            transition={{
              duration: blob.duration,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}
        <div
          className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
          style={{ backgroundImage: GRAIN }}
        />
      </div>
      {children}
    </div>
  );
}
